import { User, Phone, Mail, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export interface Manager {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: "owner" | "admin";
}

interface ManagerCardProps {
  manager: Manager;
  onDelete: (id: string) => void;
  canDelete: boolean;
  isPending?: boolean;
}

export function ManagerCard({ manager, onDelete, canDelete, isPending = false }: ManagerCardProps) {
  return (
    <Card className="h-64 w-64 shrink-0 py-0">
      <CardContent className="flex h-full flex-col justify-between p-4 pt-5">
        <div className="flex flex-col items-center">
          {/* Avatar */}
          <div className="relative mb-2 flex size-18 items-center justify-center rounded-full bg-orange-100">
            <User className="size-10 text-orange-500" />
            {isPending && (
              <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-100">
                <Clock className="h-3 w-3 text-yellow-600" />
              </div>
            )}
          </div>

          {/* Name */}
          <h3 className="mb-1 text-center text-sm font-semibold text-gray-900">
            {manager.firstName} {manager.lastName}
          </h3>

          {/* Pending badge */}
          {isPending && (
            <Badge variant="secondary" className="mb-2 text-xs">
              Invitation en attente
            </Badge>
          )}

          {/* Contact info */}
          <div className="mb-3 space-y-0.5 text-center">
            {manager.phone && (
              <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
                <Phone className="h-3 w-3" />
                <span>{manager.phone}</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
              <Mail className="h-3 w-3" />
              <span className="max-w-40 truncate">{manager.email}</span>
            </div>
          </div>
        </div>

        {/* Delete button - only show if not owner and canDelete is true */}
        {manager.role !== "owner" && canDelete && (
          <div className="mt-auto w-full">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="w-full">
                  <Trash2 className="mr-1 h-3 w-3" />
                  {isPending ? "Annuler" : "Supprimer"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {isPending ? "Annuler l'invitation" : "Confirmer la suppression"}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isPending
                      ? `Êtes-vous sûr de vouloir annuler l'invitation envoyée à ${manager.email} ?`
                      : `Êtes-vous sûr de vouloir retirer ${manager.firstName} ${manager.lastName} de la liste des gérants ?`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(manager.id)}>
                    {isPending ? "Annuler l'invitation" : "Supprimer"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
